import { useSyncExternalStore } from 'react';

/**
 * The prototype's dashboard layout settings.
 *
 * Ranges and step sizes are the prototype's own (`set-box-size` 110–220 step 5,
 * `set-magnify-strength` 1.05–1.3 step 0.01). Stored values are clamped on read
 * so a hand-edited localStorage entry can't produce a broken layout.
 *
 * One deviation: the prototype ships magnification off by default. It's on here
 * because it was asked for explicitly; the toggle still turns it off.
 */
export interface DashboardSettings {
    /** Minimum stage-card width in px. Drives --ba-stage-card-min. */
    boxSize: number;
    magnifyOn: boolean;
    magnifyStrength: number;
}

export const SETTINGS_RANGES = {
    boxSize: { min: 110, max: 220, step: 5 },
    magnifyStrength: { min: 1.05, max: 1.3, step: 0.01 },
} as const;

const DEFAULTS: DashboardSettings = {
    boxSize: 150,
    magnifyOn: true,
    magnifyStrength: 1.15,
};

const KEY = 'blackArrowDashboardSettings';

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

function normalise(raw: unknown): DashboardSettings {
    if (typeof raw !== 'object' || raw === null) {
        return DEFAULTS;
    }

    const value = raw as Partial<Record<keyof DashboardSettings, unknown>>;

    return {
        boxSize:
            typeof value.boxSize === 'number'
                ? clamp(value.boxSize, SETTINGS_RANGES.boxSize.min, SETTINGS_RANGES.boxSize.max)
                : DEFAULTS.boxSize,
        magnifyOn: typeof value.magnifyOn === 'boolean' ? value.magnifyOn : DEFAULTS.magnifyOn,
        magnifyStrength:
            typeof value.magnifyStrength === 'number'
                ? clamp(
                      value.magnifyStrength,
                      SETTINGS_RANGES.magnifyStrength.min,
                      SETTINGS_RANGES.magnifyStrength.max,
                  )
                : DEFAULTS.magnifyStrength,
    };
}

function read(): DashboardSettings {
    try {
        const stored = localStorage.getItem(KEY);

        return stored === null ? DEFAULTS : normalise(JSON.parse(stored));
    } catch {
        // Private mode, or someone put junk in the key. Defaults are fine.
        return DEFAULTS;
    }
}

let current = read();
const listeners = new Set<() => void>();

/**
 * Pushes the layout settings onto the document as CSS variables.
 *
 * These are the same two custom properties `StageGrid.module.css` reads, so the
 * grid resizes without React re-rendering it — which is what makes the
 * prototype's slider feel live as you drag.
 */
export function applyDashboardSettings(settings: DashboardSettings = current): void {
    const root = document.documentElement;

    root.style.setProperty('--ba-stage-card-min', `${String(settings.boxSize)}px`);
    // The prototype scales the value type with the box, bounded so the number
    // stays readable at either end of the slider.
    root.style.setProperty(
        '--ba-stage-font-scale',
        String(clamp(settings.boxSize / DEFAULTS.boxSize, 0.85, 1.35)),
    );
}

export function setDashboardSettings(patch: Partial<DashboardSettings>): void {
    current = normalise({ ...current, ...patch });

    try {
        localStorage.setItem(KEY, JSON.stringify(current));
    } catch {
        /* private mode — the change applies, it just doesn't persist */
    }

    applyDashboardSettings(current);

    for (const listener of listeners) {
        listener();
    }
}

export function resetDashboardSettings(): void {
    setDashboardSettings(DEFAULTS);
}

function subscribe(listener: () => void): () => void {
    listeners.add(listener);

    return () => listeners.delete(listener);
}

export function useDashboardSettings(): DashboardSettings {
    return useSyncExternalStore(
        subscribe,
        () => current,
        () => DEFAULTS,
    );
}
